import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import '../../styles/MemberSettings.css';
import ErrorPage from "../ErrorPage";
import { getMemberSettingsData, updateMemberSettingsData } from '../../api/member/MemberSettingsAPI';
import Page from "../../components/Page";
import Dropdown from '../../components/inputs/Dropdown';
import '../../styles/MemberSettings.css';
import TextInput from '../../components/inputs/TextInput';
import useSetOtherSelected from '../../hooks/useSetOtherSelected';
import { TOTAL_GRAD_YEARS_VISIBLE } from '../../constants';
import CheckboxGroup from '../../components/inputs/CheckboxGroup';
import PhoneInput from '../../components/inputs/PhoneInput';
import CustomModal from "../../components/Modal";
import { useMember } from './MemberContext';
import '../../styles/components/Button.css';
import toast from 'react-hot-toast';
import { Prev } from 'react-bootstrap/esm/PageItem';

/**
 * Text field for "Other" dropdown options
 * Prop definitions and usage are the same as the <TextInput> component.
 */
const OtherTextField = ({ id, placeholder, value, setValue, required }) => {
  return (
    <TextInput
        id={id}
        placeholder={placeholder}
        label=' '
        charLimit={80}
        value={value}
        setValue={setValue}
        required={required}
        isOther={true}
    />
  );
};

// TODO: USE user from MemberContext for placeholders/default values
// TODO: use toast after filling out data

const MemberSettingsPage = () => {
    const navigate = useNavigate();
    const [formSubmitDisabled, setFormSubmitDisabled] = useState(true);
    const [apiSuccess, setApiSuccess] = useState(true);
    const [settingsLoading, setSettingsLoading] = useState(true);

    const { member, setMember, loading } = useMember();
    const [dropdownData, setDropdownData] = useState({
        genders: [],
        majors: [],
        degrees: [],
        pronouns: [],
        races: [],
        shirtSizes: [],
    });

    const { genders, majors, degrees, pronouns, races, shirtSizes } = dropdownData;

    const [preferredName, setPreferredName] = useState('');
    const [personalEmail, setPersonalEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const [selectedGenderID, setSelectedGenderID] = useState(null);
    const [selectedRaceID, setSelectedRaceID] = useState(null);
    const [selectedMajorID, setSelectedMajorID] = useState(null);
    const [selectedDegreeID, setSelectedDegreeID] = useState(null);
    const [selectedShirtSizeID, setSelectedShirtSizeID] = useState(null);
    const [selectedPronounIDs, setSelectedPronounIDs] = useState([]);
    const [selectedGradDate, setSelectedGradDate] = useState(null);

    const [otherGenderSelected, setOtherGenderSelected] = useSetOtherSelected(genders, selectedGenderID, "dropdownValue");
    const [otherRaceSelected, setOtherRaceSelected] = useSetOtherSelected(races, selectedRaceID, "dropdownValue");
    const [otherMajorSelected, setOtherMajorSelected] = useSetOtherSelected(majors, selectedMajorID, "dropdownValue");
    const [otherShirtSizeSelected, setOtherShirtSizeSelected] = useSetOtherSelected(shirtSizes, selectedShirtSizeID, "dropdownValue");

    const [otherGender, setOtherGender] = useState('');
    const [otherMajor, setOtherMajor] = useState('');
    const [otherPronouns, setOtherPronouns] = useState('');
    const [otherRace, setOtherRace] = useState('');
    const [otherShirtSize, setOtherShirtSize] = useState('');

    const [pronounCheckboxesDisabled, setPronounCheckboxesDisabled] = useState(false);

    useEffect(() => {
        if (otherPronouns == null) {
            setOtherPronouns('');
        }
        if (otherPronouns !== '') {
            setPronounCheckboxesDisabled(true);
        } else {
            setPronounCheckboxesDisabled(false);
        }
    }, [otherPronouns]);

    useEffect(() => {
        (async () => {
            try {
                setSettingsLoading(true);
                const response = await getMemberSettingsData();
                setDropdownData(response);
            } catch(error) {
                setApiSuccess(false);
            } finally {
                setSettingsLoading(false);
            }
            
        })();
    }, []);

    useEffect(() => {
        if (member && dropdownData) {
            setPreferredName(member.preferredName);
            setPersonalEmail(member.personalEmail);
            setPhoneNumber(member.phoneNumber);

            setSelectedDegreeID(member.degreeProgram.id);
            setSelectedGenderID(member.gender.id);
            setSelectedMajorID(member.major.id);
            setSelectedRaceID(member.race.id);
            setSelectedPronounIDs(member.pronouns.map(p => p.id || []));
            setSelectedShirtSizeID(member.shirtSize.id);
            setSelectedGradDate(member.gradYear);

            setOtherGender(member.specifiedGender);
            member.specifiedGender !== null && setOtherGenderSelected(true);
            setOtherMajor(member.specifiedMajor);
            member.specifiedMajor !== null && setOtherMajorSelected(true);
            setOtherPronouns(member.specifiedPronoun);
            setOtherRace(member.specifiedRace);
            member.specifiedRace !== null && setOtherRaceSelected(true);
            setOtherShirtSize(member.specifiedShirtSize);
            member.specifiedShirtSize !== null && setOtherShirtSizeSelected(true);
        }
    }, [member, dropdownData]);

    if (settingsLoading || loading) {
        return (
            <Page>
                <div>Loading...</div>
            </Page>
        );
    }

    const validateForm = async (event) => {
        // Prevent default form submission behavior
        event.preventDefault();

        // Disable submit button after it is clicked.
        // If we leave the button enabled, a user could spam-click and send 
        // multiple API requests to register the same account.
        setFormSubmitDisabled(true);

        // Access form data
        const formData = new FormData(event.target);

        // Convert form data into a JSON format
        // TODO: make into object in backend, registration data object
        const formDataJSON = {};
        for (const entry of formData) {
            if (entry[0] === 'pronouns-group') {
                // Insert the array of selected pronoun IDs instead of the default checkbox data
                // If a custom pronoun is specified, these checboxes are disabled and won't be included in the form data
                formDataJSON['selectedPronounIDs'] = selectedPronounIDs;
            } 
            // Validate email address and phone number with a regex

            else if (!isNaN(entry[1]) && entry[1] !== '') {
                // If the entry is a number (ID), convert it from a string to a number
                formDataJSON[entry[0]] = Number(entry[1]);
            }
            else {
                // Add all other entries to the JSON as is
                formDataJSON[entry[0]] = entry[1].trim();
            }
        }

        try {
            toast.loading("Updating Settings...");
            setTimeout(async () => {
                const response = await updateMemberSettingsData(formDataJSON);
                if (response.ok) {
                    toast.dismiss();
                    setMember(response.updatedMember);
                    try {
                        toast.success("Settings updated!");
                        setFormSubmitDisabled(true);
                        navigate('/member/dashboard');
                    } catch (jsonError) {
                        console.error("Error parsing JSON:", jsonError);
                        toast.error("Error parsing server response.");
                    }
                } else {
                    toast.dismiss();
                    console.log("Error creating event. Status:", response.status);
                    toast.error("Settings failed to update. Status: " + response.status);
                }
            }, 300);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting changes:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
            setFormSubmitDisabled(false);
        }
    }

    const currentYear = new Date().getFullYear();
    const gradDateOptions = Array.from({ length: TOTAL_GRAD_YEARS_VISIBLE + 1 }, (_, i) => ({
        value: currentYear + i,
        label: String(currentYear + i),
    }));

    // Event handler for selecting pronouns
	const handlePronounSelect = (event) => {
		const changedValue = event.target.value;
		const isChecked = event.target.checked;
		// Add/remove changed checkbox from selected array
		if (isChecked) {
			setSelectedPronounIDs([...selectedPronounIDs, Number(changedValue)]);
		} else {
			setSelectedPronounIDs(selectedPronounIDs.filter((id) => Number(id) !== Number(changedValue)));
		}
	};

    return (
        <>
        {!apiSuccess && <ErrorPage />}
        {apiSuccess && (
            <Page>
                <div className="d-flex flex-row justify-content-start">
                    <Link className='link-style' style={{ textDecoration: 'none' }} to="/member/dashboard">
                    
                        <div className="back-arrow" onClick={() => navigate('/member/dashboard')}>
                            &larr; Back
                        </div>
                    </Link>
                </div>
                
                <div className='member-settings'>
                    <h2>Account Settings</h2>
                    <h4>User: {member.ritUserID}</h4>
                    <div className="setting-fields">
                        <form name='registration-form' onChange={() => setFormSubmitDisabled(false)} onSubmit={(event) => validateForm(event, "registration-form")}>
                            <div className="form-container centered">
                                <div className="column input-column">
                                    <TextInput id="preferredName" placeholder="Preferred Name" className="form-input" charLimit={80} label='Preferred Name' value={preferredName} setValue={(event) => setPreferredName(event.target.value)} />
                                    <TextInput id="personalEmail" placeholder="Personal Email" type="email" className="form-input" charLimit={80} label='Personal Email' value={personalEmail} setValue={(event) => setPersonalEmail(event.target.value)} />
                                    <PhoneInput id='phoneNumber' placeholder='Phone Number' label='Phone Number' className="form-input" initialValue={phoneNumber} />
                                    <label className='input-label' htmlFor="pronouns">Pronouns</label>
                                    <p className='note-text'>Note: Pronouns specified in the "Other" field will override any checkbox selections.</p>
                                    <CheckboxGroup
                                        id="pronouns"
                                        name="pronouns-group"
                                        options={
                                            pronouns.map((p) => {
                                                return {
                                                    value: p.id,
                                                    label: p.dropdownValue,
                                                }
                                            })
                                        }
                                        onChange={handlePronounSelect}
                                        groupDisabled={pronounCheckboxesDisabled}
                                        selectedValues={selectedPronounIDs}
                                    />
                                    <OtherTextField id="otherPronoun" placeholder="Other" value={otherPronouns} setValue={(event) => setOtherPronouns(event.target.value)} />
                                </div>
                                <div className="column">
                                    <Dropdown
                                        id="gender"
                                        name="genderID"
                                        options={genders.map(g => ({ value: g.id, label: g.dropdownValue }))}
                                        onChange={e => setSelectedGenderID(e.target.value)}
                                        className="form-input"
                                        label='Gender'
                                        value={selectedGenderID}
                                    />
                                    {otherGenderSelected && <OtherTextField id="otherGender" placeholder="Gender" value={otherGender} setValue={(event) => setOtherGender(event.target.value)} required />}
                                    <Dropdown
                                        id="race"
                                        name="raceID"
                                        options={races.map(r => ({ value: r.id, label: r.dropdownValue }))}
                                        onChange={e => setSelectedRaceID(e.target.value)}
                                        className="form-input"
                                        label='Race'
                                        value={selectedRaceID}
                                    />
                                    {otherRaceSelected && <OtherTextField id="otherRace" placeholder="Race" value={otherRace} setValue={(event) => setOtherRace(event.target.value)} required />}
                                    <Dropdown
                                        id="major"
                                        name="majorID"
                                        options={majors.map(m => ({ value: m.id, label: m.dropdownValue }))}
                                        onChange={e => setSelectedMajorID(e.target.value)}
                                        className="form-input"
                                        label='Major'
                                        value={selectedMajorID}
                                    />
                                    {otherMajorSelected && <OtherTextField id="otherMajor" placeholder="Major" value={otherMajor} setValue={(event) => setOtherMajor(event.target.value)} required />}
                                    <Dropdown
                                        id="degree"
                                        name="degreeID"
                                        options={degrees.map(d => ({ value: d.id, label: d.dropdownValue }))}
                                        onChange={e => setSelectedDegreeID(e.target.value)}
                                        className="form-input"
                                        label='Degree Program'
                                        value={selectedDegreeID}
                                    />
                                    <Dropdown
                                        id="grad-year"
                                        name="gradYear"
                                        options={gradDateOptions}
                                        value={selectedGradDate}
                                        onChange={e => setSelectedGradDate(e.target.value)}
                                        className="form-input"
                                        label='Expected Graduation Date'
                                    />
                                    <Dropdown
                                        id="tshirt"
                                        name="shirtSizeID"
                                        options={shirtSizes.map(s => ({ value: s.id, label: s.dropdownValue }))}
                                        onChange={e => setSelectedShirtSizeID(e.target.value)}
                                        className="form-input"
                                        label='T-Shirt Size'
                                        value={selectedShirtSizeID}
                                    />
                                    {otherShirtSizeSelected && <OtherTextField id="otherShirtSize" placeholder="Shirt Size" value={otherShirtSize} setValue={(event) => setOtherShirtSize(event.target.value)} required />}
                                </div>
                            </div>
                            <h4 id="co-op">
                                <span className="bold-text">Report CO-OP / Class Conflict:</span>{" "}
                                <a
                                    href="https://docs.google.com/forms/d/e/1FAIpQLScW5DMegUPq60miIg_hAo4yO3gXLEoanIjXZCuuEiC3fVZnmw/viewform"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link-style"
                                >
                                    Report Form Link
                                </a>
                            </h4>
                            <div className="submit-container">
                                <input
                                    type='submit'
                                    className='button primary-btn'
                                    value="Save & Exit"
                                    disabled={formSubmitDisabled}
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </Page>
        )}
        </>
    );
};

export default MemberSettingsPage;
